import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Shield, Eye, EyeOff, Mail, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { authApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { FooterMinimal } from '../components/Footer';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: '',
  });
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleForgotPassword = async () => {
    if (!forgotEmail) {
      toast.error('Veuillez entrer votre email');
      return;
    }
    setIsSendingReset(true);
    try {
      await authApi.requestPasswordReset(forgotEmail, 'admin');
      setResetSent(true);
      toast.success('Lien de réinitialisation envoyé !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginForm.email || !loginForm.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await authApi.login({
        email: loginForm.email,
        password: loginForm.password,
        role: 'admin',
      });
      
      const userData = {
        id: response.data.user_id,
        role: response.data.role,
        name: response.data.name,
        company_id: response.data.company_id,
      };
      
      login(userData, response.data.token);
      toast.success(`Bienvenue, ${response.data.name}!`);
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Identifiants incorrects');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-heading font-semibold">Administration</h1>
          <p className="text-muted-foreground mt-2">Accès réservé aux administrateurs</p>
        </div>

        <Card className="shadow-floating border-border/60 animate-fade-in" style={{ animationDelay: '100ms' }}>
          <CardHeader className="text-center pb-4">
            <CardTitle className="font-heading text-xl">Connexion Admin</CardTitle>
            <CardDescription>
              Entrez vos identifiants administrateur
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="admin-email-input"
                  placeholder="admin@resawork.fr"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    data-testid="admin-password-input"
                    placeholder="••••••••"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground cursor-pointer rounded-md hover:bg-muted transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="text-right">
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-primary hover:underline"
                  onClick={() => setShowForgotPassword(true)}
                >
                  Mot de passe oublié ?
                </button>
              </div>
              
              <Button
                type="submit"
                className="w-full rounded-full"
                data-testid="admin-login-btn"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Connexion...
                  </>
                ) : (
                  'Se connecter'
                )}
              </Button>
            </form>
            
            <div className="mt-6 pt-4 border-t text-center">
              <Link to="/login" className="text-sm text-muted-foreground hover:text-primary">
                Retour à la connexion
              </Link>
            </div>
          </CardContent>
        </Card>

        <div className="animate-fade-in mt-6" style={{ animationDelay: '200ms' }}>
          <FooterMinimal />
        </div>
      </div>

      {/* Forgot Password Modal */}
      <Dialog open={showForgotPassword} onOpenChange={(open) => {
        setShowForgotPassword(open);
        if (!open) {
          setForgotEmail('');
          setResetSent(false);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              Mot de passe oublié
            </DialogTitle>
            <DialogDescription>
              Entrez votre adresse email administrateur
            </DialogDescription>
          </DialogHeader>
          
          {resetSent ? (
            <div className="py-6 text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Email envoyé !</h3>
                <p className="text-sm text-muted-foreground mt-2">
                  Si un compte admin existe avec l'adresse <span className="font-medium">{forgotEmail}</span>, 
                  vous recevrez un lien de réinitialisation.
                </p>
              </div>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => setShowForgotPassword(false)}
              >
                Retour
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Adresse email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="admin@resawork.fr"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleForgotPassword()}
                />
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowForgotPassword(false)}
                >
                  Annuler
                </Button>
                <Button 
                  className="flex-1"
                  onClick={handleForgotPassword}
                  disabled={isSendingReset}
                >
                  {isSendingReset ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Envoi...
                    </>
                  ) : (
                    'Envoyer'
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
